import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

export const MODAL_TYPES = {
    PACKAGE: 'package',
    CREATE_PATIENT: 'createPatient',
    RENAME_PATIENT: 'renamePatient',
    RENAME_FILE: 'renameFile',
    DELETE_FILE: 'deleteFile',
    DELETE_PATIENT: 'deletePatient',
    MOVE_FILE_TO_PATIENT: 'moveFileToPatient',
};

const ModalContext = createContext({
    activeModal: null,
    modalPayload: null,
    patientsVersion: 0,
    openModal: () => { },
    closeModal: () => { },
    isModalOpen: () => false,
    refreshPatients: () => { },
});

export const ModalProvider = ({ children }) => {
    const location = useLocation();
    const [activeModal, setActiveModal] = useState(null);
    const [modalPayload, setModalPayload] = useState(null);
    const [patientsVersion, setPatientsVersion] = useState(0);

    const openModal = useCallback((modalName, payload = null) => {
        setActiveModal(modalName);
        setModalPayload(payload);
    }, []);

    const closeModal = useCallback(() => {
        setActiveModal(null);
        setModalPayload(null);
    }, []);

    useEffect(() => {
        const isPublicAuthRoute = (
            location.pathname === '/auth'
            || location.pathname.startsWith('/verify-email/')
            || location.pathname.startsWith('/reset-password/')
        );

        if (isPublicAuthRoute) {
            closeModal();
        }
    }, [closeModal, location.pathname]);

    const isModalOpen = useCallback((modalName) => activeModal === modalName, [activeModal]);
    const refreshPatients = useCallback(() => {
        setPatientsVersion((currentValue) => currentValue + 1);
    }, []);

    const value = useMemo(() => ({
        activeModal,
        modalPayload,
        patientsVersion,
        openModal,
        closeModal,
        isModalOpen,
        refreshPatients,
    }), [activeModal, closeModal, patientsVersion, isModalOpen, modalPayload, openModal, refreshPatients]);

    return (
        <ModalContext.Provider value={value}>
            {children}
        </ModalContext.Provider>
    );
};

export const useModal = () => useContext(ModalContext);

export const usePackageModal = () => {
    const { modalPayload, openModal, closeModal, isModalOpen } = useModal();

    return {
        isOpen: isModalOpen(MODAL_TYPES.PACKAGE),
        payload: isModalOpen(MODAL_TYPES.PACKAGE) ? modalPayload : null,
        openModal: (payload = null) => openModal(MODAL_TYPES.PACKAGE, payload),
        closeModal,
    };
};

export const useMoveFileToPatientModal = () => {
    const { modalPayload, openModal, closeModal, isModalOpen } = useModal();

    return {
        isOpen: isModalOpen(MODAL_TYPES.MOVE_FILE_TO_PATIENT),
        payload: isModalOpen(MODAL_TYPES.MOVE_FILE_TO_PATIENT) ? modalPayload : null,
        openModal: (payload = null) => openModal(MODAL_TYPES.MOVE_FILE_TO_PATIENT, payload),
        closeModal,
    };
};

export const useCreatePatientModal = () => {
    const { modalPayload, openModal, closeModal, isModalOpen } = useModal();

    return {
        isOpen: isModalOpen(MODAL_TYPES.CREATE_PATIENT),
        payload: isModalOpen(MODAL_TYPES.CREATE_PATIENT) ? modalPayload : null,
        openModal: (payload = null) => openModal(MODAL_TYPES.CREATE_PATIENT, payload),
        closeModal,
    };
};

export const usePatientRefresh = () => {
    const { patientsVersion, refreshPatients } = useModal();

    return {
        patientsVersion,
        refreshPatients,
    };
};

export const useRenamePatientModal = () => {
    const { modalPayload, openModal, closeModal, isModalOpen } = useModal();

    return {
        isOpen: isModalOpen(MODAL_TYPES.RENAME_PATIENT),
        payload: isModalOpen(MODAL_TYPES.RENAME_PATIENT) ? modalPayload : null,
        openModal: (payload = null) => openModal(MODAL_TYPES.RENAME_PATIENT, payload),
        closeModal,
    };
};

export const useDeletePatientModal = () => {
    const { modalPayload, openModal, closeModal, isModalOpen } = useModal();

    return {
        isOpen: isModalOpen(MODAL_TYPES.DELETE_PATIENT),
        payload: isModalOpen(MODAL_TYPES.DELETE_PATIENT) ? modalPayload : null,
        openModal: (payload = null) => openModal(MODAL_TYPES.DELETE_PATIENT, payload),
        closeModal,
    };
};

export const useDeleteFileModal = () => {
    const { modalPayload, openModal, closeModal, isModalOpen } = useModal();

    return {
        isOpen: isModalOpen(MODAL_TYPES.DELETE_FILE),
        payload: isModalOpen(MODAL_TYPES.DELETE_FILE) ? modalPayload : null,
        openModal: (payload = null) => openModal(MODAL_TYPES.DELETE_FILE, payload),
        closeModal,
    };
};

export const useRenameFileModal = () => {
    const { modalPayload, openModal, closeModal, isModalOpen } = useModal();

    return {
        isOpen: isModalOpen(MODAL_TYPES.RENAME_FILE),
        payload: isModalOpen(MODAL_TYPES.RENAME_FILE) ? modalPayload : null,
        openModal: (payload = null) => openModal(MODAL_TYPES.RENAME_FILE, payload),
        closeModal,
    };
};
